import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { addDays } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AuthModule from '../../../../auth/AuthModule';
import { useCustomerContractTypeSettings } from '../../../../services/customerContractTypeSettings';
import { jhTheme } from '../../../../themeProvider';
import { InternalContractDetail } from '../../../BaseController/types/internalContract';
import { CustomButton } from '../../../common/button/customButton';
import { Utils } from '../../../common/utils/utils';

export interface AdjustEquipmentFormProps {
  currentEquipment: InternalContractDetail;
  setShowBusy: (busy: boolean) => void;
}

export const AdjustEquipmentForm = (props: AdjustEquipmentFormProps) => {
  const { currentEquipment, setShowBusy } = props;

  const [newContractUsage, setContractUsage] = useState('');
  const [requestedChangeDate, setRequestedChangeDate] = useState<Date>(
    new Date(),
  );
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

  const navigate = useNavigate();

  const {
    CustomerContractTypeSettingsData,
    isLoading: isLoadingCustomerContractTypeSettingsData,
  } = useCustomerContractTypeSettings(
    String(currentEquipment.customerNumber),
    String(currentEquipment.contractUsageName),
  );

  const checkDateIsAllowed = (date: Date) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() - 1);

    if (
      date < Utils.parseDateStringToDate(currentEquipment.endDate) &&
      date > Utils.parseDateStringToDate(currentEquipment.beginDate) &&
      date > currentDate
    ) {
      return true;
    }
    return false;
  };

  const checkMandatory = () => {
    if (checkDateIsAllowed(requestedChangeDate) && newContractUsage) {
      setButtonDisabled(false);
      return true;
    }
    setButtonDisabled(true);
    return false;
  };

  useEffect(() => {
    checkMandatory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newContractUsage, requestedChangeDate]);

  useEffect(() => {
    if (CustomerContractTypeSettingsData) {
      setRequestedChangeDate(
        addDays(new Date(), CustomerContractTypeSettingsData.cancelPeriodDays),
      );
    }
  }, [CustomerContractTypeSettingsData]);

  const sendData = async () => {
    const contractNumber = currentEquipment.contractNr;
    const { contractPosition } = currentEquipment;

    const obj = {
      contractNumber,
      contractPosition,
      requestedChangeDate,
      newContractUsage,
      customerNote: '',
    };

    setShowBusy(true);

    try {
      const b2cToken = await AuthModule.getInstance().getProfileTokenRedirect();
      const resp = await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACKEND_API_URL}/contractchangerequests/usage-change`,
        data: obj,
        headers: {
          Authorization: `Bearer ${b2cToken}`,
          'Ocp-Apim-Subscription-Key': `${process.env.REACT_APP_API_KEY}`,
        },
      });
      if (resp?.status === 201) {
        navigate(`/detail/${contractNumber}/${contractPosition}/X`);
        setShowBusy(false);
      }
    } catch (error) {
      setShowBusy(false);
    }
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          bgcolor: 'rgb(224,228,232)',
        }}
      >
        <CardContent>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={deLocale}
          >
            <Stack
              direction="row"
              spacing={2}
              alignContent="center"
              justifyContent="space-around"
            >
              <Stack sx={{ width: '100%' }} spacing={2}>
                <Stack
                  direction="column"
                  alignItems="flex-start"
                  sx={{ width: '100%' }}
                >
                  <Stack sx={{ width: '100%' }}>
                    <Typography variant="buttonActionHeadline">
                      {t('ButtonContentAdjustEquipment')}
                    </Typography>
                    <Divider light />
                  </Stack>
                </Stack>
                {!isLoadingCustomerContractTypeSettingsData ? (
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {t('ContractType')}:
                      </Typography>
                      <Stack>
                        <Select
                          size="small"
                          value={newContractUsage}
                          onChange={(event) => {
                            setContractUsage(event.target.value);
                          }}
                          className="TextField-without-border-radius"
                          sx={{
                            svg: { color: '#000' },
                            bgcolor: '#fff',
                          }}
                        >
                          {currentEquipment.contractUsageName !==
                          'coreFleet' ? (
                            <MenuItem value="coreFleet">
                              {t('coreFleet')}
                            </MenuItem>
                          ) : null}
                          {currentEquipment.contractUsageName !==
                          'plannedPeak' ? (
                            <MenuItem value="plannedPeak">
                              {t('plannedPeak')}
                            </MenuItem>
                          ) : null}
                          {currentEquipment.contractUsageName !==
                          'adhocPeak' ? (
                            <MenuItem value="adhocPeak">
                              {t('adhocPeak')}
                            </MenuItem>
                          ) : null}
                        </Select>
                      </Stack>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {t('ChangeDate')}:
                      </Typography>
                      <Stack>
                        <DatePicker
                          disablePast
                          maxDate={Utils.parseDateStringToDate(
                            currentEquipment.endDate,
                          )}
                          slotProps={{
                            textField: {
                              className: 'TextField-without-border-radius',
                              sx: {
                                backgroundColor:
                                  jhTheme.palette.background.paper,
                              },
                              size: 'small',
                            },
                          }}
                          value={requestedChangeDate}
                          onChange={(newValue) => {
                            if (newValue) {
                              setRequestedChangeDate(newValue);
                            }
                          }}
                          format={t('DateFormatShort') || ''}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                ) : (
                  <Stack sx={{ width: '100%' }} spacing={2}>
                    <CircularProgress color="inherit" />
                  </Stack>
                )}

                <Stack>
                  {!isLoadingCustomerContractTypeSettingsData &&
                  CustomerContractTypeSettingsData ? (
                    <Stack>
                      <Stack direction="row">
                        {CustomerContractTypeSettingsData.cancelPeriodDays ? (
                          <Stack direction="row" spacing={2}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {t('DaysNoticePeriod')}:
                            </Typography>
                            <Typography variant="caption">
                              {
                                CustomerContractTypeSettingsData.cancelPeriodDays
                              }{' '}
                              {t('Days')}
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                      <Stack direction="row">
                        {CustomerContractTypeSettingsData.minDays ? (
                          <Stack direction="row" spacing={2}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {t('MinimumTerm')}:
                            </Typography>
                            <Typography variant="caption">
                              {CustomerContractTypeSettingsData.minDays}{' '}
                              {t('Days')}
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                    </Stack>
                  ) : null}
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                  <CustomButton
                    handleOnClick={sendData}
                    text={t('RequestChange')}
                    sx={{
                      fontFamily: 'JHFontBold',
                      border: '1px solid transparent',
                    }}
                    color="primary"
                    size="small"
                    disabled={buttonDisabled}
                  />
                </Stack>
              </Stack>
            </Stack>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </Stack>
  );
};
